/**
 * Product Image Carousel - Reusable JavaScript Module
 * 
 * Description: Generic JavaScript functionality for product image carousel navigation
 *              (arrows and dots) that can be reused across the website.
 * 
 * Usage: Include this script and initialize carousels
 * 
 * Features:
 * - Infinite looping navigation
 * - Arrow and dot navigation
 * - Hover-based visibility
 * - Works with any image container
 */

(function() {
  'use strict';

  /**
   * Initialize product image carousel
   * 
   * @param {string} imageId - ID of the main image element
   * @param {string} carouselId - Unique identifier for this carousel
   * @param {Object} options - Configuration options
   * @returns {void}
   */
  window.initProductImageCarousel = function(imageId, carouselId, options) {
    options = options || {};
    const prefix = options.prefix || 'product-carousel__';
    const containerSelector = options.containerSelector || '.product-image-carousel';
    
    const imageElement = document.getElementById(imageId);
    if (!imageElement) return;
    
    const container = imageElement.closest(containerSelector);
    if (!container) return;
    
    // Get carousel data
    const dotsContainer = container.querySelector('.' + prefix + 'dots');
    if (!dotsContainer) return;
    
    const totalImages = parseInt(dotsContainer.getAttribute('data-total-images')) || 0;
    if (totalImages === 0) return;
    
    const galleryImagesJson = dotsContainer.getAttribute('data-gallery-images');
    if (!galleryImagesJson || galleryImagesJson.trim() === '' || galleryImagesJson === '[]') {
      return;
    }
    
    // Store carousel data
    imageElement.setAttribute('data-carousel-id', carouselId);
    imageElement.setAttribute('data-total-images', totalImages);
    imageElement.setAttribute('data-gallery-images', galleryImagesJson);
  };

  /**
   * Navigate carousel (previous/next) with infinite looping
   * 
   * @param {string} imageId - ID of the main image element
   * @param {string} direction - 'prev' or 'next'
   * @param {Object} options - Configuration options
   * @returns {void}
   */
  window.navigateProductImageCarousel = function(imageId, direction, options) {
    options = options || {};
    const prefix = options.prefix || 'product-carousel__';
    const containerSelector = options.containerSelector || '.product-image-carousel';
    
    const imageElement = document.getElementById(imageId);
    if (!imageElement) return;
    
    const container = imageElement.closest(containerSelector);
    if (!container) return;
    
    // Try to get total images from image element, dots container, or parent card
    let totalImages = parseInt(imageElement.getAttribute('data-total-images')) || 0;
    if (totalImages === 0) {
      const dotsContainer = container.querySelector('.' + prefix + 'dots');
      if (dotsContainer) {
        totalImages = parseInt(dotsContainer.getAttribute('data-total-images')) || 0;
      }
    }
    
    // Fallback: check parent card (for collection page compatibility)
    if (totalImages === 0) {
      const card = imageElement.closest('[data-total-images]');
      if (card) {
        totalImages = parseInt(card.getAttribute('data-total-images')) || 0;
      }
    }
    
    if (totalImages === 0) return;
    
    const currentIndex = parseInt(imageElement.getAttribute('data-image-index')) || 0;
    
    // Infinite loop: Use modulo to wrap around
    let newIndex;
    if (direction === 'next') {
      newIndex = (currentIndex + 1) % totalImages;
    } else {
      newIndex = (currentIndex - 1 + totalImages) % totalImages;
    }
    
    window.goToProductImageByImageId(imageId, newIndex, options);
  };

  /**
   * Go to specific product image by index (generic version)
   * Works with image IDs directly
   * 
   * @param {string} imageId - ID of the main image element
   * @param {number} imageIndex - Target image index
   * @param {Object} options - Configuration options
   * @returns {void}
   */
  window.goToProductImageByImageId = function(imageId, imageIndex, options) {
    options = options || {};
    const prefix = options.prefix || 'product-carousel__';
    const containerSelector = options.containerSelector || '.product-image-carousel';
    
    const imageElement = document.getElementById(imageId);
    if (!imageElement) return;
    
    const container = imageElement.closest(containerSelector);
    if (!container) return;
    
    // Try to get gallery images from image element, dots container, or parent card
    let galleryImagesJson = imageElement.getAttribute('data-gallery-images');
    if (!galleryImagesJson || galleryImagesJson.trim() === '' || galleryImagesJson === '[]') {
      const dotsContainer = container.querySelector('.' + prefix + 'dots');
      if (dotsContainer) {
        galleryImagesJson = dotsContainer.getAttribute('data-gallery-images');
      }
    }
    
    // Fallback: check parent card (for collection page compatibility)
    if (!galleryImagesJson || galleryImagesJson.trim() === '' || galleryImagesJson === '[]') {
      const card = imageElement.closest('[data-gallery-images]');
      if (card) {
        galleryImagesJson = card.getAttribute('data-gallery-images');
      }
    }
    
    if (!galleryImagesJson || galleryImagesJson.trim() === '' || galleryImagesJson === '[]') {
      return;
    }
    
    try {
      const galleryImages = JSON.parse(galleryImagesJson);
      
      if (!Array.isArray(galleryImages) || galleryImages.length === 0) {
        return;
      }
      
      if (galleryImages[imageIndex] === undefined) {
        return;
      }
      
      const imageUrl = galleryImages[imageIndex];
      
      // Build srcset: 1x uses smaller width, 2x uses original
      let srcsetUrl = imageUrl;
      if (imageUrl.includes('width=684')) {
        srcsetUrl = imageUrl.replace('width=684', 'width=342');
      } else if (imageUrl.includes('width=')) {
        const widthMatch = imageUrl.match(/width=(\d+)/);
        if (widthMatch) {
          const currentWidth = parseInt(widthMatch[1]);
          const halfWidth = Math.floor(currentWidth / 2);
          srcsetUrl = imageUrl.replace(/width=\d+/, 'width=' + halfWidth);
        }
      }
      
      // Update main image
      imageElement.src = imageUrl;
      imageElement.srcset = srcsetUrl + ' 1x, ' + imageUrl + ' 2x';
      imageElement.setAttribute('data-image-index', imageIndex);
      
      // Update active carousel dot
      window.updateCarouselDots(container, imageIndex, prefix);
      
      // Update active thumbnail if visible (for collection page compatibility)
      const thumbnails = container.querySelectorAll('.product-thumbnail');
      if (thumbnails.length > 0) {
        thumbnails.forEach(function(t) {
          t.classList.remove('active');
          const thumbIndex = parseInt(t.getAttribute('data-thumbnail-index'));
          if (!isNaN(thumbIndex) && thumbIndex === imageIndex) {
            t.classList.add('active');
          }
        });
      }
    } catch (e) {
      // Silently fail if JSON parsing fails
    }
  };

  /**
   * Update carousel dots active state
   * 
   * @param {HTMLElement} container - Container element (can be container or card)
   * @param {number} activeIndex - Index of active image
   * @param {string} prefix - CSS class prefix (optional)
   * @returns {void}
   */
  window.updateCarouselDots = function(container, activeIndex, prefix) {
    // If prefix is not provided, try to detect it or use default
    if (!prefix) {
      // Check if it's a card element (collection page style)
      const dots = container.querySelectorAll('.product-card__carousel__dot');
      if (dots.length > 0) {
        dots.forEach(function(dot, index) {
          if (index === activeIndex) {
            dot.classList.add('product-card__carousel__dot--active');
          } else {
            dot.classList.remove('product-card__carousel__dot--active');
          }
        });
        return;
      }
      prefix = 'product-carousel__';
    }
    
    // Try to find dots using the prefix
    const dots = container.querySelectorAll('.' + prefix + 'dot');
    if (dots.length === 0) {
      // Fallback: try to find any dot with the prefix pattern
      const allDots = container.querySelectorAll('[class*="' + prefix.replace('__', '') + '__dot"]');
      allDots.forEach(function(dot, index) {
        if (index === activeIndex) {
          // Add active class - try different patterns
          dot.classList.add(prefix + 'dot--active');
          dot.classList.add(prefix.replace('__', '') + '__dot--active');
        } else {
          dot.classList.remove(prefix + 'dot--active');
          dot.classList.remove(prefix.replace('__', '') + '__dot--active');
        }
      });
      return;
    }
    
    dots.forEach(function(dot, index) {
      if (index === activeIndex) {
        dot.classList.add(prefix + 'dot--active');
      } else {
        dot.classList.remove(prefix + 'dot--active');
      }
    });
  };

  /**
   * Navigate product carousel using productId (for collection page compatibility)
   * 
   * @param {string} productId - Product ID
   * @param {string} direction - 'prev' or 'next'
   * @param {Event} event - Click event object
   * @returns {void}
   */
  window.navigateProductCarousel = function(productId, direction, event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    const imageId = 'product-image-' + productId;
    const options = {
      prefix: 'product-card__carousel__',
      containerSelector: '.product-card'
    };
    
    if (window.navigateProductImageCarousel) {
      window.navigateProductImageCarousel(imageId, direction, options);
    }
  };

  /**
   * Go to specific product image using productId (for collection page compatibility)
   * 
   * @param {string} productId - Product ID
   * @param {number} imageIndex - Target image index
   * @param {Event} event - Click event object
   * @returns {void}
   */
  window.goToProductImage = function(productId, imageIndex, event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    const imageId = 'product-image-' + productId;
    const options = {
      prefix: 'product-card__carousel__',
      containerSelector: '.product-card'
    };
    
    if (window.goToProductImageByImageId) {
      window.goToProductImageByImageId(imageId, imageIndex, options);
    }
  };

  /**
   * Initialize all carousels on page load using event delegation
   */
  function attachProductImageCarouselDelegation() {
    // Carousel arrow and dot click handling using event delegation
    document.addEventListener('click', function(e) {
      const target = e.target;
      let handled = false;
      
      // Handle arrow clicks - check for button with data-carousel-id attribute
      let arrow = target.closest('button[data-carousel-id]');
      
      // If clicked on SVG or path inside arrow button, find the button
      if (!arrow && (target.tagName === 'svg' || target.tagName === 'path')) {
        arrow = target.closest('button[data-carousel-id]');
      }
      
      // Also check if target itself is the button
      if (!arrow && target.tagName === 'BUTTON' && target.hasAttribute('data-carousel-id')) {
        arrow = target;
      }
      
      if (arrow && arrow.hasAttribute('data-carousel-id')) {
        const arrowClass = arrow.className || '';
        // Only process if it's an arrow button (has arrow in class name)
        if (arrowClass.includes('arrow')) {
          e.preventDefault();
          e.stopPropagation();
          
          const imageId = arrow.getAttribute('data-carousel-id');
          const direction = arrowClass.includes('--prev') || arrowClass.includes('prev') ? 'prev' : 'next';
          
          // Determine prefix from class name - default to collection page prefix
          let prefix = 'product-card__carousel__';
          let containerSelector = '.product-card';
          
          // Check if it's the collection page carousel
          if (arrowClass.includes('product-card__carousel')) {
            prefix = 'product-card__carousel__';
            containerSelector = '.product-card';
          } else if (arrowClass.match(/([\w-]+)__arrow/)) {
            const match = arrowClass.match(/([\w-]+)__arrow/);
            if (match) {
              prefix = match[1] + '__';
              containerSelector = '.product-image-carousel';
            }
          }
          
          if (window.navigateProductImageCarousel) {
            try {
              window.navigateProductImageCarousel(imageId, direction, { 
                prefix: prefix,
                containerSelector: containerSelector
              });
              handled = true;
            } catch (err) {
              console.error('Carousel navigation error:', err);
            }
          } else {
            console.warn('navigateProductImageCarousel function not found');
          }
        }
      }
      
      // Handle dot clicks - check for button with data-image-index attribute
      if (!handled) {
        let dot = target.closest('button[data-image-index]');
        
        // If target itself is the button
        if (!dot && target.tagName === 'BUTTON' && target.hasAttribute('data-image-index')) {
          dot = target;
        }
        
        if (dot && dot.hasAttribute('data-image-index')) {
          const dotClass = dot.className || '';
          // Only process if it's a dot button (has dot in class but not dots)
          if (dotClass.includes('dot') && !dotClass.includes('dots')) {
            e.preventDefault();
            e.stopPropagation();
            
            const dotsContainer = dot.closest('[class*="dots"]');
            if (!dotsContainer) return false;
            
            const imageId = dotsContainer.getAttribute('data-carousel-id');
            if (!imageId) return false;
            
            const imageIndex = parseInt(dot.getAttribute('data-image-index'));
            if (isNaN(imageIndex)) return false;
            
            // Determine prefix from class name - default to collection page prefix
            let prefix = 'product-card__carousel__';
            let containerSelector = '.product-card';
            
            // Check if it's the collection page carousel
            if (dotClass.includes('product-card__carousel')) {
              prefix = 'product-card__carousel__';
              containerSelector = '.product-card';
            } else if (dotClass.match(/([\w-]+)__dot/)) {
              const match = dotClass.match(/([\w-]+)__dot/);
              if (match) {
                prefix = match[1] + '__';
                containerSelector = '.product-image-carousel';
              }
            }
            
            if (window.goToProductImageByImageId) {
              try {
                window.goToProductImageByImageId(imageId, imageIndex, { 
                  prefix: prefix,
                  containerSelector: containerSelector
                });
              } catch (err) {
                console.error('Carousel dot navigation error:', err);
              }
            } else {
              console.warn('goToProductImageByImageId function not found');
            }
            return false;
          }
        }
      }
    });
  }

  function scheduleProductImageCarouselInit() {
    if (window.Shopify && window.Shopify.designMode) {
      attachProductImageCarouselDelegation();
      return;
    }
    const run = () => attachProductImageCarouselDelegation();
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(run, { timeout: 3000 });
    } else if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', run, { once: true });
    } else {
      run();
    }
  }

  scheduleProductImageCarouselInit();
})();
